// modules
import moment from "moment";
import { useEffect, useMemo, useCallback, useState } from "react";

// constants
import { REIMBURSMENT_DECL_VERIFICATION as Constants, REVIEW_RETRO_CLAIM_FORM, Routes, StatusInfo } from "constants/global";
import SearchFilterIcon from "components/atoms/SearchFilterIcon/SearchFilterIcon";
import { REIMB_VERIFICATION_COLUMN_DATA, REIMB_APPROVAL_COLUMN_DATA } from "constants/reimbVerification";

// redux
import { getReimbVoucherGroupList, loadingVoucherGroups, reimbVoucherGroupListSelector } from "redux/reimbursement/reimbursementSlice";
import { showHideFilter, updateCurrentGroup, toggleFilter, getUserPermissionListSelector, getConfigData, getConfigSelector } from "redux/userList/userSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Type
import { StatusBarInfoProps } from "containers/ReimbursementVerification/ReimbursementVerification";
import { ColumnHeaderRowType } from "components/atoms/ExpandedRowTable/ExpandedRowTable";
import { FilterItem } from "constants/retrospectiveTypes";

// styles
import styles from "containers/ReimbursementVerification/reimbursementVerification.module.css";

// utils
import { formatReimbVerificationData, getAllVoucherTypes, getReimbVerificationRecordCount } from "utils/formatReimbVerificationData";

export type GroupDetails = {
	voucher_group_code: number;
	voucher_group_desc: string;
};

export type ReimbursementVerificationProps = {
	pathname: string;
};

export type ReimbursementVerificationTypes = {
	getGroupHeader: (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, data?: any) => void;
	generateCols: () => void;
	handleExpandRows: (val: string) => void;
	currentExpandedGroup: string | undefined;
	defaultGroupKey: string;
	updatedReimbGroupList?: Array<GroupDetails>;
	statusBarInfo: StatusBarInfoProps;
	columnHeaderData: {
		[key: string]: Array<ColumnHeaderRowType>;
	};
	loadingVouchers: boolean;
};

export const statusMap: { [key: string]: string } = {
	[Routes.DECLARATION_VERIFICATION]: Constants.DEFAULT_DECLARATION_VERIFICATION_STATUS,
	[Routes.DECLARATION_APPROVAL]: REVIEW_RETRO_CLAIM_FORM.CLAIM_STATUS_PENDING_APPROVAL
};

export function useReimbursementVerification({ pathname }: ReimbursementVerificationProps): ReimbursementVerificationTypes {
	const dispatch = useAppDispatch();
	const reimbVoucherGroupList = useAppSelector(reimbVoucherGroupListSelector);
	const showFilter = useAppSelector(showHideFilter);
	const loadingVouchers = useAppSelector(loadingVoucherGroups) === 1;
	const userPermissionList = useAppSelector(getUserPermissionListSelector);
	const configDetails = useAppSelector(getConfigSelector);
	const [currentExpandedGroup, setCurrentExpandedGroup] = useState<string>();
	const filterData: FilterItem = {};

	const columnHeaderData = {
		"/declaration-verification": REIMB_VERIFICATION_COLUMN_DATA,
		"/declaration-approval": REIMB_APPROVAL_COLUMN_DATA
	};
	const { data: configDetailsData } = configDetails || {};

	useEffect(() => {
		getVoucherGroupList();
		setCurrentExpandedGroup(undefined);
		if (pathname === Routes.DECLARATION_VERIFICATION || pathname === Routes.DECLARATION_APPROVAL) {
			dispatch(getConfigData({ config_mst_code: Constants.CONFIG_MASTER_CODE_VOUCHER_TYPES }));
		}
	}, [pathname]);

	const getVoucherGroupList = useCallback(() => {
		const payload = {
			reqPayload: {
				from_year_month: moment(moment().subtract(6, "months").calendar()).format("YYYYMM"),
				to_year_month: moment(moment().subtract(1, "months").calendar()).format("YYYYMM"),
				reimb_status: statusMap[pathname]
			},
			pathName: pathname
		};
		dispatch(getReimbVoucherGroupList(payload));
	}, [dispatch, getReimbVoucherGroupList, reimbVoucherGroupList, pathname]);

	const reimbVerificationGroupAccess = useMemo(() => {
		return userPermissionList?.filter(({ perm_group_code }) => perm_group_code === Constants.REIMB_VOUCHER_GROUP_ACCESS);
	}, [userPermissionList]);

	// voucher groups
	const updatedReimbGroupList = useMemo(() => {
		if (reimbVoucherGroupList?.groupList[pathname]?.length > 0) {
			dispatch(updateCurrentGroup(reimbVoucherGroupList?.groupList[pathname][0]?.voucher_group_desc));
		}
		return pathname === Routes.DECLARATION_VERIFICATION ? formatReimbVerificationData(reimbVerificationGroupAccess) : getAllVoucherTypes(configDetailsData);
	}, [reimbVoucherGroupList, configDetailsData, reimbVerificationGroupAccess, formatReimbVerificationData, pathname]);

	const statusBarInfo = useMemo((): StatusBarInfoProps => {
		const statusStr = pathname === Routes.DECLARATION_VERIFICATION ? Constants.VERIFICATION_STATUS : Constants.APPROVAL_STATUS;
		const pendingCount =
			pathname === Routes.DECLARATION_VERIFICATION ? getReimbVerificationRecordCount(reimbVerificationGroupAccess, reimbVoucherGroupList) : reimbVoucherGroupList?.recordCount;
		if (pendingCount) {
			return {
				message:
					pendingCount > 1 ? (
						<p>
							There are currently <u>{pendingCount}</u> declarations requiring {` `}
							{statusStr}
						</p>
					) : (
						<p>
							There is currently <u>{pendingCount}</u> declaration requiring {` `}
							{statusStr}
						</p>
					),
				background: "yellow"
			};
		} else {
			return {
				message: <p>{StatusInfo.NO_ACTION_REQ}</p>,
				background: "green"
			};
		}
	}, [reimbVoucherGroupList, getReimbVerificationRecordCount, pathname]);

	const defaultGroupKey = updatedReimbGroupList?.[0]?.voucher_group_code;

	const handleToggleFilter = (groupCode: string) => {
		filterData[groupCode] = !showFilter[groupCode];
		dispatch(updateCurrentGroup(groupCode));
		dispatch(toggleFilter(filterData));
	};

	const getGroupHeader = (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: Array<GroupDetails>) => {
		return (
			updatedReimbGroupList &&
			groupData &&
			groupData.map((item: GroupDetails) => {
				const key = item.voucher_group_code;

				return {
					cell1: <p className={styles.groupTitle}>{item.voucher_group_desc}</p>,
					cell2: (
						<SearchFilterIcon
							defaultSelectedFilter={Constants.DEFAULT_STATUS}
							defaultSelectedFilterValues={[Constants.DEFAULT_STATUS[0].value]}
							label={"FILTER"}
							groupCode={item.voucher_group_desc}
							onClick={() => handleToggleFilter(item.voucher_group_desc)}
						/>
					),
					key: key,
					id: key
				};
			})
		);
	};
	const generateCols = () => {
		return [
			{
				dataIndex: "cell1",
				title: <p></p>,
				width: "90%"
			},
			{
				dataIndex: "cell2",
				title: <p></p>
			}
		];
	};

	const handleExpandRows = useCallback(
		(_value: string) => {
			setCurrentExpandedGroup(_value);
		},
		[currentExpandedGroup]
	);

	return {
		defaultGroupKey,
		updatedReimbGroupList,
		getGroupHeader,
		generateCols,
		statusBarInfo,
		currentExpandedGroup,
		handleExpandRows,
		columnHeaderData,
		loadingVouchers
	};
}
